import { compose, filter, get, identity, includes, map, pick, pickBy, reject, size } from 'lodash/fp';
import { createSelector } from 'reselect';
import { ResourceDataDiscriminator } from '@mews-commander/resources/src';
import { getStringOrUndefinedProp, getStringProp, getStringArrayProp } from '@mews/fp';
import { mapId, createOptionsSelector, getOrEmptyObject, notEmpty } from '@mews-commander/utils';
import { getSelectedEntityId } from '../../entityManagement/selectors';
import { moduleSelector } from '../module';
import { getCategories } from './categories';
import { getFloorName, getFloors } from './floors';

const parentResourcesSelector = createSelector(moduleSelector, get('resourcesStep'));

export const getResources = compose(get('resources'), parentResourcesSelector);

const getMeta = compose(get('meta'), parentResourcesSelector);

export const getBulkOperationsResourceIds = compose(get('operations'), parentResourcesSelector);

export const isResourceSelected = createSelector(getStringProp('resourceId'), getBulkOperationsResourceIds, includes);

export const isBulkOperationEnabled = compose(notEmpty, getBulkOperationsResourceIds);

export const getSelectedResource = createSelector(getResources, getSelectedEntityId, (resources, id) => resources[id]);

export const getSelectedResourceNumber = compose(get('number'), getSelectedResource);

export const getSelectedResourceIndex = compose(get('index'), getSelectedResource);

export const getResource = createSelector(getStringProp('id'), getResources, getOrEmptyObject);

export const getResourcesByIds = createSelector(getStringArrayProp('resourceIds'), getResources, pick);

export const getResourceName = compose(get('name'), getResource);

const getCategoryResources = createSelector(getResources, getStringOrUndefinedProp('id'), (resources, categoryId) =>
    filter({ categoryId })(resources)
);

export const getCategoryResourceIds = compose(mapId, getCategoryResources);

const getAllCategoryResources = createSelector(
    getResources,
    getStringProp('id'),
    getStringProp('childCategoryId'),
    (resources, categoryId, childCategoryId) => [
        ...filter({ categoryId })(resources),
        ...filter({ categoryId: childCategoryId })(resources),
    ]
);

const getCategoryFloorResources = createSelector(
    getCategoryResources,
    getStringOrUndefinedProp('floorId'),
    (resources, floorId) => filter({ floorId })(resources)
);

export const getCategoryFloorResourcesCount = compose(size, getCategoryFloorResources);

export const getSelectedCategoryIdFromMeta = compose(get('selectedCategoryId'), getMeta);

const getSelectedCategoryFromMeta = createSelector(
    getCategories,
    getSelectedCategoryIdFromMeta,
    (categories, id) => categories[id]
);

export const getSelectedCategoryNameFromMeta = compose(get('name'), getSelectedCategoryFromMeta);

export const getSelectedFloorId = compose(get('selectedFloorId'), getMeta);

const getSelectedFloorFromMeta = createSelector(getFloors, getSelectedFloorId, (floors, id) => floors[id]);

export const getSelectedFloorNameFromMeta = compose(get('name'), getSelectedFloorFromMeta);

const getSelectedFloorResources = createSelector(getResources, getSelectedFloorId, (resources, floorId) =>
    filter({ floorId })(resources)
);

const getSelectedCategorySelectedFloorResources = createSelector(
    getSelectedFloorResources,
    getSelectedCategoryIdFromMeta,
    (resources, categoryId) => filter({ categoryId })(resources)
);

const getUnselectedResources = createSelector(
    getSelectedFloorResources,
    getSelectedEntityId,
    getSelectedResourceIndex,
    (resources, id, index) => compose(reject({ parentResourceIndex: index }), reject({ id }))(resources)
);

export const getParentResourceOptions = createOptionsSelector(getUnselectedResources, {
    value: 'id',
    label: 'name',
    secondaryText: 'categoryId',
});

export const getRecursiveChildrenResourceIds = createSelector(
    getResources,
    getStringArrayProp('parentResourceBuilderIds'),
    (resources, parentIds) => {
        const childrenIds = [];
        let currentChildrenIds = findChildResourceIds(resources, parentIds);

        while (notEmpty(currentChildrenIds)) {
            childrenIds.push(...currentChildrenIds);
            currentChildrenIds = findChildResourceIds(resources, currentChildrenIds);
        }

        return childrenIds;
    }
);

const findChildResourceIds = (resources: $TSFixMe, parentIds: $TSFixMe) =>
    compose(
        mapId,
        // @ts-expect-error $TSFixMeError: No overload matches this call.
        filter(({ parentResourceBuilderId }) => includes(parentResourceBuilderId, parentIds))
    )(resources);

export const getSelectedCategorySelectedFloorResourceIds = compose(mapId, getSelectedCategorySelectedFloorResources);

export const getResourceCountByFloor = createSelector(
    getResources,
    getStringOrUndefinedProp('floorId'),
    (resources, floorId) =>
        compose(
            size,
            filter({ floorId }),
            pickBy((value) => !(value as $TSFixMe).parentResourceBuilderId)
        )(resources)
);

export const getTotalResourcesCount = createSelector(getResources, (resources) =>
    compose(
        size,
        pickBy((value) => !(value as $TSFixMe).parentResourceBuilderId)
    )(resources)
);

export const getResourceCountByCategory = compose(size, getAllCategoryResources);

export const getResourceIdsByFloorId = createSelector(getResources, getStringOrUndefinedProp('id'), (resources, id) =>
    compose(mapId, filter({ floorId: id }))(resources)
);

export const getResourcesExportData = createSelector(getResources, identity, (resources, state) =>
    map(
        ({
            // omitted props
            categoryId,
            floorId,
            number,
            parentResourceBuilderId,
            parentResourceIndex,
            resourceCategoryIndexes,
            ...otherData
        }) => ({
            data: {
                discriminator: ResourceDataDiscriminator.Space,
                value: {
                    floorNumber: getFloorName(state, { id: floorId }),
                },
            },
            enterpriseIndex: 0,
            name: number,
            parentResourceBuilderId,
            parentResourceIndex,
            resourceCategoryIndexes,
            ...otherData,
        })
    )(resources)
);

export const getManualResourceFormInitialValues = createSelector(
    getSelectedFloorNameFromMeta,
    getSelectedCategoryNameFromMeta,
    getSelectedResource,
    (floor, category, resource) => ({ ...resource, floor, category })
);
